/**
 * Die ziehbare Trennleiste zwischen Produktliste und Produktakte.
 *
 * WARUM ES SIE GIBT.
 *
 * Die Liste hatte eine feste Breite (320 bis 480 px, je nach Fenster). Wer
 * an einer Akte arbeitet, will die Liste schmal; wer Artikel vergleicht und
 * sucht, will sie breit. Eine feste Breite ist fuer beide falsch.
 *
 * WAS SIE TUT.
 *
 *   – Ziehen mit Maus, Stift oder Finger (Pointer-Ereignisse, eine Quelle).
 *   – Pfeiltasten links/rechts verschieben in Schritten -- die Leiste ist ein
 *     `separator` mit Fokus, nicht nur ein Maus-Ziel.
 *   – Doppelklick setzt auf die Vorgabe zurueck. Einklappen gibt es bewusst
 *     noch nicht; die Vorgabebreite ist der Rueckweg.
 *   – Die gewaehlte Breite wird lokal gemerkt. Sie ist eine Vorliebe dieses
 *     Bildschirms, keine Einstellung des Kontos -- ein Laptop und ein
 *     Grossbildschirm brauchen verschiedene Breiten.
 *
 * DIE GRENZEN. Die Liste wird nie schmaler als ihre Suchzeile braucht, die
 * Akte nie schmaler als ihre Kopfzeile. Die Breite wird bei jeder Bewegung
 * gegen beide geprueft -- so entsteht nie eine waagerechte Bildlaufleiste
 * der ganzen Seite.
 */
import { useCallback, useEffect, useRef } from "react";
import { useI18n } from "../i18n/index.tsx";

export const LISTE_MIN = 320;
export const AKTE_MIN = 520;
const SCHRITT = 24;
const SPEICHER = "homebase.listeBreite";

/** Die gemerkte Breite -- oder `null`, wenn keine gemerkt ist oder der Speicher fehlt. */
export function gemerkteListenbreite(): number | null {
  try {
    const v = Number(window.localStorage.getItem(SPEICHER));
    return Number.isFinite(v) && v >= LISTE_MIN ? v : null;
  } catch { return null; }
}

function merke(breite: number | null): void {
  try {
    if (breite === null) window.localStorage.removeItem(SPEICHER);
    else window.localStorage.setItem(SPEICHER, String(Math.round(breite)));
  } catch { /* Privater Modus -- dann eben nicht gemerkt. */ }
}

export function Splitter({ container, breite, onBreite }: {
  /** Der Behaelter, dessen Breite die Obergrenze bestimmt. */
  container: React.RefObject<HTMLDivElement | null>;
  /** Die aktuelle Listenbreite in Pixeln -- `null` heisst Vorgabe. */
  breite: number | null;
  onBreite: (b: number | null) => void;
}): React.JSX.Element {
  const t = useI18n().t;
  const zieht = useRef(false);

  /** Eine gewuenschte Breite auf das Zulaessige bringen. */
  const begrenze = useCallback((wunsch: number): number => {
    const gesamt = container.current?.getBoundingClientRect().width ?? 0;
    const hoechstens = Math.max(LISTE_MIN, gesamt - AKTE_MIN);
    return Math.min(Math.max(wunsch, LISTE_MIN), hoechstens);
  }, [container]);

  /* Waehrend des Ziehens gehoert die Bewegung dem Fenster, nicht der Leiste --
     sonst reisst sie ab, sobald der Zeiger schneller ist als das Layout. */
  useEffect(() => {
    const bewegt = (e: PointerEvent): void => {
      if (!zieht.current || !container.current) return;
      const links = container.current.getBoundingClientRect().left;
      onBreite(begrenze(e.clientX - links));
    };
    const los = (): void => {
      if (!zieht.current) return;
      zieht.current = false;
      document.body.classList.remove("app--zieht");
      const aktuell = container.current?.querySelector<HTMLElement>(".list")?.getBoundingClientRect().width;
      merke(aktuell ?? null);
    };
    window.addEventListener("pointermove", bewegt);
    window.addEventListener("pointerup", los);
    return () => {
      window.removeEventListener("pointermove", bewegt);
      window.removeEventListener("pointerup", los);
    };
  }, [begrenze, container, onBreite]);

  /* Wird das Fenster schmaler, darf eine gemerkte Breite die Akte nicht
     unter ihr Minimum druecken. */
  useEffect(() => {
    const passt = (): void => { if (breite !== null) onBreite(begrenze(breite)); };
    window.addEventListener("resize", passt);
    return () => window.removeEventListener("resize", passt);
  }, [begrenze, breite, onBreite]);

  const jetzt = (): number =>
    breite ?? container.current?.querySelector<HTMLElement>(".list")?.getBoundingClientRect().width ?? LISTE_MIN;

  return (
    <div className="app__splitter" role="separator" aria-orientation="vertical"
      aria-label={t("split.label")} title={t("split.hint")} tabIndex={0}
      aria-valuemin={LISTE_MIN} aria-valuenow={Math.round(jetzt())}
      onPointerDown={(e) => {
        e.preventDefault();
        zieht.current = true;
        document.body.classList.add("app--zieht");
      }}
      onDoubleClick={() => { onBreite(null); merke(null); }}
      onKeyDown={(e) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const neu = begrenze(jetzt() + (e.key === "ArrowLeft" ? -SCHRITT : SCHRITT));
        onBreite(neu);
        merke(neu);
      }} />
  );
}
